/**
 * @module routes.Sessions.AllCoachesPublicSession._components.useCoachNextSlots
 *
 * The soonest bookable time for each coach on the Coaches tab, for the chip that
 * replaced the count of session types.
 *
 * Two round trips per coach, and no fewer: `/available_dates` says which days a coach
 * has, and only `/coach_profiles/:id/available_times` says when on a given day. The
 * dates call is asked once per coach because `slot_duration` and `lesson_rate_id` are
 * a property of the coach's own default option, so the plural `coach_profile_ids` the
 * endpoint accepts cannot be used to batch coaches with different options — the same
 * limitation `useFacilityAvailability` documents.
 *
 * A coach whose day turns out to hold no upcoming slot is not chased into the next
 * day: that would be an unbounded walk down the calendar for a chip. They read as
 * having no availability in the window, which is also what the calendar behind the
 * card will show.
 */
import { useEffect, useState } from "react";
import { DateTime } from "luxon";

import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { AvailableSlotModel } from "app/models/scheduling/AvailableSlotModel";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";

import { bookableRates } from "./coachMeta";
import { NextSlot, earliestSlotFrom, nextAvailableDate } from "./coachNextSlot";

/** How far ahead a coach is looked at before the chip gives up. */
const WINDOW_DAYS = 60;

export interface CoachNextSlots {
  /** Coach `id` → their soonest slot, or null once looked up and found empty. */
  nextSlots: Map<string, NextSlot>;
  loading: boolean;
}

/** Same choice of option as the rest of the facility screens. */
const availabilityRate = (coach: CoachProfileModel) => {
  const rates = bookableRates(coach);
  return rates.find((rate) => rate.defaultRate) || rates[0] || null;
};

const useCoachNextSlots = (
  coaches: CoachProfileModel[],
  isPublicPage = true,
): CoachNextSlots => {
  const [nextSlots, setNextSlots] = useState<Map<string, NextSlot>>(new Map());
  const [loading, setLoading] = useState(false);

  // The ids the two endpoints disagree on — see useFacilityAvailability.
  const coachDatesId = (coach: CoachProfileModel) => coach.id;
  const coachTimesId = (coach: CoachProfileModel) =>
    isPublicPage ? coach.calendarUuid : coach.id;

  // Recompute only when the set of coaches changes, not on every render of the list.
  const coachKey = coaches.map((coach) => coach.id).join(",");

  useEffect(() => {
    if (coaches.length === 0) {
      setNextSlots(new Map());
      return undefined;
    }

    let active = true;
    setLoading(true);

    const now = DateTime.local();
    const from = now.toISODate();
    const to = now.plus({ days: WINDOW_DAYS }).toISODate();

    const lookups = coaches.map((coach) => {
      const rate = availabilityRate(coach);
      if (!rate) {
        return Promise.resolve<[string, NextSlot]>([coach.id, null]);
      }

      const query = {
        slot_duration: rate.durationInMinutes,
        lesson_rate_id: rate.id,
      };

      return schedulingFetchGet(
        "/available_dates",
        { from, to, coach_profile_ids: [coachDatesId(coach)], ...query },
        {},
        null,
        isPublicPage,
      )
        .then((profiles: any) => {
          const dates = (profiles || []).flatMap(
            (profile: any) => profile?.dates || [],
          );
          const day = nextAvailableDate(dates, now);
          if (!day) {
            return null;
          }

          return schedulingFetchGet(
            `/coach_profiles/${coachTimesId(coach)}/available_times`,
            {
              from: DateTime.fromISO(day).startOf("day").toISO(),
              to: DateTime.fromISO(day).endOf("day").toISO(),
              coach_profile_ids: [coachTimesId(coach)],
              ...query,
            },
            {},
            null,
            isPublicPage,
          ).then((raw: any) =>
            earliestSlotFrom(
              (raw?.[0]?.slots || []).map(
                (slot: any) => new AvailableSlotModel(slot),
              ),
              now,
            ),
          );
        })
        .then((slot: NextSlot) => [coach.id, slot] as [string, NextSlot])
        // One coach's availability failing must not blank the whole grid; that
        // coach's chip simply says nothing is available.
        .catch(() => [coach.id, null] as [string, NextSlot]);
    });

    Promise.all(lookups)
      .then((entries) => {
        if (active) {
          setNextSlots(new Map(entries));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachKey, isPublicPage]);

  return { nextSlots, loading };
};

export default useCoachNextSlots;
